import { iterateEntries } from "../../../../../Common/IterableUtils";
import { IFetchFunctionCallContextProvider } from "../../Form/CustomizationContexts/FetchFunctionContext/IFetchFunctionCallContextProvider";
import { ViewModel } from "../CoreTypes";
import {
    isAttributeRequireNormalization,
    isConstValueSource,
    isFetchFunctionValueSource,
    isTakeFromModelValueSource,
    NodeDataDeclaration,
    NodeFieldValueSources,
} from "../DataDeclaration/DataDeclaration";
import { DataDeclarationHelper } from "../DataDeclaration/DataDeclarationHelper";
import { IModel } from "../ModelStoring/Model";
import { Sections } from "../ModelStoring/Sections";
import { MaskResolver } from "../PathUtils/MaskResolver";
import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    emptyAbsoluteModelPath,
    PathToken,
    PathTokens,
} from "../../../../../Common/ModelPath/ModelPath";
import { SectionName } from "../../../Controls/FormParts/Unit/UnitId/UnitId";
import { InvalidProgramStateError } from "../../../../../Common/Errors";

interface ModelPathTrieNode {
    leafNode?: boolean;
    children: Map<PathToken, ModelPathTrieNode>;
}

export class ModelNormalizer {
    private readonly dataDeclarationHelper: DataDeclarationHelper;
    private readonly isReadOnly: boolean;
    private readonly fetchFunctionCallContextProvider: IFetchFunctionCallContextProvider;
    private readonly sections: Sections;

    public constructor(
        fetchFunctionCallContextProvider: IFetchFunctionCallContextProvider,
        dataDeclarationHelper: DataDeclarationHelper,
        sections: Sections,
        isReadOnly: boolean
    ) {
        this.isReadOnly = isReadOnly;
        this.fetchFunctionCallContextProvider = fetchFunctionCallContextProvider;
        this.dataDeclarationHelper = dataDeclarationHelper;
        this.sections = sections;
    }

    public normalizeSection(model: IModel, sectionName: SectionName): void {
        this.sections.removeSectionFromIgnore(sectionName);
        const sectionPrefix = this.sections.getSectionPrefix(sectionName);
        if (sectionPrefix != undefined) {
            this.normalizeByPrefix(model, sectionPrefix);
        } else {
            const masks = this.sections.masks(sectionName);
            this.normalizeTrie(model, this.buildPathsTrie(masks));
        }
    }

    public normalizeByPrefix(model: IModel, nodePathPrefix: AbsoluteResolvedModelPath): void {
        const paths = MaskResolver.getPathsByPrefix(this.dataDeclarationHelper, model, nodePathPrefix);
        this.normalizeTrie(model, this.buildPathsTrie([...paths]));
    }

    private normalizeTrie(model: IModel, trie: ModelPathTrieNode): void {
        this.normalizeTrieNode(model, trie, emptyAbsoluteModelPath());
    }

    private normalizeTrieNode(model: IModel, trieNode: ModelPathTrieNode, nodePath: AbsoluteResolvedModelPath): void {
        if (trieNode.leafNode) {
            if (!this.sections.isIgnoredPathByMasks(nodePath)) {
                this.normalizeViewModelAtPath(model, nodePath);
            }

            if (this.dataDeclarationHelper.isNodeHasChildrenEntry(nodePath)) {
                const instances = model.getChildren(nodePath) ?? [];
                for (const instance of instances) {
                    this.normalizeByPrefix(model, nodePath.joinWith(instance));
                }
            }
        }
        this.normalizeDescendants(model, nodePath, trieNode);
    }

    private normalizeDescendants(
        model: IModel,
        nodePath: AbsoluteResolvedModelPath,
        trieNode: ModelPathTrieNode
    ): void {
        for (const [pathToken, trieChild] of trieNode.children.entries()) {
            if (PathTokens.isInstanceToken(pathToken) && trieNode.children.has(PathTokens.each)) {
                continue;
            }
            if (PathTokens.isMultiToken(pathToken)) {
                if (!nodePath.isResolved()) {
                    throw new InvalidProgramStateError();
                }
                const instances = model.getChildren(nodePath) ?? [];
                for (const instance of instances) {
                    this.normalizeTrieNode(model, trieChild, nodePath.joinWith(instance));
                }
            } else {
                this.normalizeTrieNode(model, trieChild, nodePath.joinWith(pathToken));
            }
        }
    }

    private buildPathsTrie(paths: Iterable<AbsoluteModelPath>): ModelPathTrieNode {
        const trieRootNode: ModelPathTrieNode = { children: new Map() };
        for (const path of paths) {
            let node = trieRootNode;
            for (const token of path.getPathParts()) {
                let nextNode = node.children.get(token);
                if (nextNode == undefined) {
                    nextNode = { children: new Map() };
                    node.children.set(token, nextNode);
                }
                node = nextNode;
            }
            node.leafNode = true;
        }
        return trieRootNode;
    }

    private normalizeViewModelAtPath(model: IModel, nodePath: AbsoluteResolvedModelPath): void {
        const nodeDataDeclaration = this.dataDeclarationHelper.getEntry(nodePath);

        const viewModelUpdates: ViewModel = {};
        for (const [fieldName, dataSources] of iterateEntries(nodeDataDeclaration)) {
            if (
                dataSources != undefined &&
                this.isAttributeRequireNormalization(fieldName, nodeDataDeclaration, model, nodePath)
            ) {
                this.normalizeField(model, nodePath, fieldName, dataSources as NodeFieldValueSources, viewModelUpdates);
            }
        }

        for (const [field, value] of iterateEntries(viewModelUpdates)) {
            if (field === "children") {
                // NOTE Просто поверим, что всё получится
                const instances = value as string[];
                model.setChildrenWithoutNormalization(nodePath, instances);
            } else {
                model.setViewModelField(nodePath, field, value);
            }
        }
    }

    private isAttributeRequireNormalization(
        fieldName: keyof NodeDataDeclaration,
        nodeDataDeclaration: NodeDataDeclaration,
        model: IModel,
        nodePath: AbsoluteResolvedModelPath
    ) {
        return (
            isAttributeRequireNormalization(fieldName) &&
            !(
                fieldName === "value" &&
                nodeDataDeclaration.hasDisableValueAutoInitFlag &&
                model.getViewModelField(nodePath, "disableValueAutoInit") === "true"
            )
        );
    }

    private normalizeField(
        model: IModel,
        nodePath: AbsoluteResolvedModelPath,
        fieldName: string,
        dataSources: NodeFieldValueSources,
        viewModelUpdates: ViewModel
    ): void {
        for (const dataSource of dataSources) {
            if (isConstValueSource(dataSource)) {
                // @ts-ignore
                viewModelUpdates[fieldName] = dataSource;
                break;
            } else if (isTakeFromModelValueSource(dataSource)) {
                const modelValue = model.getViewModelField(nodePath, fieldName);
                if (modelValue != undefined) {
                    break;
                }
            } else if (isFetchFunctionValueSource(dataSource)) {
                if (this.isReadOnly) {
                    break;
                }
                const fnResult = dataSource.fn.call(
                    this.fetchFunctionCallContextProvider.getFetchFunctionCallContext(model),
                    nodePath.toLegacyPath()
                );
                if (fnResult !== undefined) {
                    // @ts-ignore
                    viewModelUpdates[fieldName] = fnResult;
                    break;
                }
            } else {
                throw new Error(
                    `Unknown data source descriptors in data declaration. FieldName: ${fieldName}. Value source: ${JSON.stringify(
                        dataSource
                    )}`
                );
            }
        }
    }
}
